<template>
  <table class="w-full">
    <thead>
      <tr>
        <!-- «Seleccionar todo» marca todo lo filtrado, no solo esta página (ADSREC-0029). -->
        <th v-if="selection" class="px-2 py-2 bg-white align-top w-8">
          <input type="checkbox" class="row-check" :checked="selection.allSelected"
            :indeterminate="selection.partlySelected" :title="t('export.selection.selectAll')"
            :aria-label="t('export.selection.selectAll')" @change="selection.toggleAll()">
        </th>
        <th v-for="field in fields" :key="field.key"
          class="px-2 py-2 text-left text-xs font-medium text-[#42A9C2] bg-white uppercase tracking-wider align-top">
          {{ field.label }}
          <!-- El filtro va dentro de la celda para heredar su alineación -->
          <TableFilterControl
            v-if="definitionFor(field.key)"
            :definition="definitionFor(field.key)"
            :label="field.label"
            :model-value="filters[field.key]"
            @change="onFilterInput(field.key, $event)"
          />
        </th>
      </tr>
      <tr v-if="filterError">
        <th :colspan="fields.length + (selection ? 1 : 0)" class="px-2 pb-2 bg-white text-left font-normal">
          <p class="text-xs text-red-600 normal-case">{{ filterError }}</p>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(item, rowIndex) in items" :key="item.id || rowIndex" class="hover:bg-gray-50 cursor-pointer"
        @click="route ? $router.push(`${route}/${item.id}`) : null">
        <td v-if="selection" class="px-2 py-2 first:rounded-l-lg w-8" @click.stop>
          <input type="checkbox" class="row-check" :checked="selection.isSelected(item)"
            :aria-label="t('export.selection.selectRow')" @change="selection.toggle(item)">
        </td>
        <td v-for="field in fields" :key="field.key"
          class="px-2 py-2 text-sm text-gray-500 first:rounded-l-lg last:rounded-r-lg text-left">
          <!-- Redirección en el campo CUPS -->
          <template v-if="field.key === 'cups'">
            <slot :name="`cell-${field.key}`" :item="item">
              <router-link :to="getCupsDetailPath(item)" class="text-blue-600 underline hover:text-blue-800"
                @click.stop>
                {{ getDisplayValue(item, field.key) }}
              </router-link>
            </slot>
          </template>
          <template v-else-if="field.key === 'cliente'">
            <slot :name="`cell-${field.key}`" :item="item">
              <router-link v-if="getAdminDetailPath(item)" :to="getAdminDetailPath(item)" class="text-blue-600 underline hover:text-blue-800" @click.stop>
                {{ getDisplayValue(item, field.key) }}
              </router-link>
              <span v-else>
                {{ getDisplayValue(item, field.key) }}
              </span>
            </slot>
          </template>
          <template v-else-if="showViewMoreButton && field.key === 'opciones'">
            <button
              v-if="route"
              @click.stop="handleDetail(item)"
              class="bg-[#2AB4D9] text-white px-3 py-1.5 rounded-lg shadow text-xs font-medium transition-all duration-300 cursor-pointer hover:bg-[#25B0D7] hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#25B0D7]"
              title="Ver más"
            >
              {{ t('common.tableActions.view_more') }}
            </button>
          </template>
          <template v-else-if="showOptions && field.key === 'opciones'">
            <div class="relative inline-block" @click.stop>
              <button
                @click="toggleDropdown(getRowKey(item, rowIndex), $event)"
                class="bg-[#2AB4D9] text-white px-3 py-1.5 rounded-lg shadow flex items-center gap-2 transition-all duration-300 cursor-pointer hover:bg-[#25B0D7] hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#25B0D7]"
                title="Opciones"
              >
                <span class="text-xs font-medium">Opciones</span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 transition-transform duration-300"
                  :class="{ 'rotate-180': isDropdownOpen(getRowKey(item, rowIndex)) }" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <Teleport to="body">
                <Transition name="dropdown">
                  <div
                    v-show="isDropdownOpen(getRowKey(item, rowIndex))"
                    class="z-50 w-56 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
                    :style="dropdownStyle"
                  >
                    <ul class="py-2">
                      <li v-if="route && !fileDownload">
                        <button
                          @click="handleDetail(item)"
                          class="w-full text-left px-4 py-3 hover:bg-[#f0f9fb] transition-colors duration-150 text-gray-700 font-medium cursor-pointer"
                        >
                          {{ t('common.tableActions.view_details') }}
                        </button>
                      </li>

                      <li v-if="fileDownload && item.is_available === false">
                        <span class="block px-4 py-3 text-gray-400 cursor-not-allowed">
                          {{ t('common.fileUnavailable') }}
                        </span>
                      </li>

                      <li v-else-if="fileDownload">
                        <button
                          @click="handlePreview(item)"
                          class="w-full text-left px-4 py-3 hover:bg-[#f0f9fb] transition-colors duration-150 text-gray-700 font-medium cursor-pointer"
                        >
                          {{ t('common.tableActions.preview_file') }}
                        </button>

                        <button
                          @click="handleDownload(item)"
                          class="w-full text-left px-4 py-3 hover:bg-[#f0f9fb] transition-colors duration-150 text-gray-700 font-medium cursor-pointer"
                        >
                          {{ t('common.tableActions.download_file') }}
                        </button>
                      </li>

                      <!-- Anular un archivo subido por error: el back decide en qué archivos (ADSREC-0026). -->
                      <li v-if="fileDownload && item.can_void">
                        <button
                          @click="handleVoidFile(item)"
                          class="w-full text-left px-4 py-3 hover:bg-red-50 transition-colors duration-150 text-red-600 font-medium cursor-pointer"
                        >
                          {{ t('requestFiles.void.action') }}
                        </button>
                      </li>

                      <li v-for="option in filteredListOptions" :key="`${getRowKey(item, rowIndex)}-${option.key || option.path || option.label}`">
                        <button
                          @click="handleListOption(item, option)"
                          class="w-full text-left px-4 py-3 hover:bg-[#f0f9fb] transition-colors duration-150 text-gray-700 font-medium cursor-pointer"
                        >
                          {{ option.label }}
                        </button>
                      </li>
                    </ul>
                  </div>
                </Transition>
              </Teleport>
            </div>
          </template>
          <template v-else-if="field.key === 'file'">
            <span v-if="item.file && item.is_available === false" class="text-gray-400" :title="t('common.fileUnavailable')">
              {{ extractFileName(item.file) }}
            </span>
            <span v-else-if="item.file">
              <a href="#" @click.stop.prevent="openFile(item)" class="text-blue-600 underline hover:text-blue-800">
                {{ extractFileName(item.file) }}
              </a>
            </span>
            <span v-else class="text-gray-400">-</span>
          </template>

          <template v-else>
            <span :class="['truncate block', field.key === 'address' ? 'max-w-[420px]' : 'max-w-[260px]']" :title="getDisplayValue(item, field.key)">
              {{ getDisplayValue(item, field.key) }}
            </span>
          </template>
        </td>
      </tr>
    </tbody>
  </table>

  <div v-if="openDropdownKey !== null" @click="closeDropdown" class="fixed inset-0 z-40"></div>

  <div v-if="previewUrl" class="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
    <div class="bg-white w-full max-w-5xl max-h-[90vh] rounded-lg shadow-xl flex flex-col overflow-hidden">
      <div class="flex items-center justify-between gap-4 px-4 py-3 border-b">
        <h3 class="text-sm font-semibold text-gray-700 truncate">{{ previewFileName }}</h3>
        <div class="flex items-center gap-2">
          <button
            @click="downloadPreviewFile"
            class="bg-[#2AB4D9] text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-[#25B0D7]"
          >
            {{ t('common.tableActions.download_file') }}
          </button>
          <button
            @click="closePreview"
            class="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-700 border hover:bg-gray-50"
          >
            {{ t('common.close') }}
          </button>
        </div>
      </div>

      <div class="bg-gray-100 overflow-auto h-[75vh] flex items-center justify-center">
        <iframe
          v-if="previewKind === 'pdf'"
          :src="previewUrl"
          class="w-full h-full bg-white"
          :title="previewFileName"
        ></iframe>
        <img
          v-else-if="previewKind === 'image'"
          :src="previewUrl"
          :alt="previewFileName"
          class="max-w-full max-h-full object-contain"
        />
        <div v-else class="text-center text-gray-600 px-6">
          <p class="mb-4">{{ t('common.previewUnavailable') }}</p>
          <button
            @click="downloadPreviewFile"
            class="bg-[#2AB4D9] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#25B0D7]"
          >
            {{ t('common.tableActions.download_file') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from 'vue-router';
import TableFilterControl from '@/components/table/TableFilterControl.vue';
import { typeLabel } from '@/services/typeLabels';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { toast } from 'vue3-toastify';

const { t, te } = useI18n();
const openDropdownKey = ref(null)
const dropdownPosition = ref({ top: 0, left: 0, width: 224 })
const router = useRouter()
const previewUrl = ref(null)
const previewItem = ref(null)
const previewMimeType = ref('')

const definitionFor = (key) => props.filterDefinitions.find((definition) => definition.key === key);

function onFilterInput(key, { value, immediate }) {
  emit('update:filters', { ...props.filters, [key]: value });
  emit('filter-change', { key, value, immediate });
}

const getRowKey = (item, rowIndex) => item?.id ?? `row-${rowIndex}`;

const isDropdownOpen = (rowKey) => openDropdownKey.value === rowKey;

const getDropdownEstimatedHeight = () => {
  const fileActions = props.fileDownload ? 2 + (props.items.some((item) => item?.can_void) ? 1 : 0) : 0;
  const actionCount = fileActions + (props.route && !props.fileDownload ? 1 : 0) + filteredListOptions.value.length;
  return 16 + (actionCount * 48);
};

const setDropdownPosition = (button) => {
  const rect = button.getBoundingClientRect();
  const margin = 8;
  const menuWidth = Math.min(224, window.innerWidth - (margin * 2));
  const estimatedHeight = getDropdownEstimatedHeight();
  const opensUp = rect.bottom + margin + estimatedHeight > window.innerHeight;

  dropdownPosition.value = {
    top: opensUp ? Math.max(margin, rect.top - estimatedHeight - margin) : rect.bottom + margin,
    left: Math.min(
      Math.max(margin, rect.right - menuWidth),
      window.innerWidth - menuWidth - margin
    ),
    width: menuWidth
  };
};

const toggleDropdown = (rowKey, event) => {
  if (openDropdownKey.value === rowKey) {
    closeDropdown();
    return;
  }

  setDropdownPosition(event.currentTarget);
  openDropdownKey.value = rowKey;
};

const closeDropdown = () => {
  openDropdownKey.value = null;
};

const dropdownStyle = computed(() => ({
  position: 'fixed',
  top: `${dropdownPosition.value.top}px`,
  left: `${dropdownPosition.value.left}px`,
  width: `${dropdownPosition.value.width}px`
}));

onMounted(() => {
  window.addEventListener('scroll', closeDropdown, true);
  window.addEventListener('resize', closeDropdown);
});

onUnmounted(() => {
  window.removeEventListener('scroll', closeDropdown, true);
  window.removeEventListener('resize', closeDropdown);
});

const handleDetail = (item) => {
  closeDropdown();
  if (item?.id && props.route) {
    router.push(`${props.route}/${item.id}`);
  }
};

const handleDownload = async (item) => {
  closeDropdown();
  console.log('Downloading file:', item);
  try {
    const response = await fetchFileBlob(item, 'attachment');

    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = item.file_name || 'downloaded_file';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);

    toast.success('Archivo descargado correctamente');
  } catch (error) {
    console.error('Error downloading file:', error);
    toast.error('Error al descargar el archivo');
  }
};

const openFile = async (item) => {
  try {
    const response = await fetchFileBlob(item, 'inline');

    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  } catch (error) {
    console.error('Error opening file:', error);
    // 404: el documento no está en el servidor (p. ej. del legacy y sin localizar).
    toast.error(error?.response?.status === 404 ? t('common.fileUnavailable') : 'Error al abrir el archivo');
  }
};

const handlePreview = async (item) => {
  closeDropdown();
  closePreview();

  try {
    const response = await fetchFileBlob(item, 'inline');
    const mimeType = response.headers?.['content-type'] || '';
    const blob = new Blob([response.data], { type: mimeType || undefined });

    previewItem.value = item;
    previewMimeType.value = mimeType;
    previewUrl.value = window.URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error previewing file:', error);
    toast.error(t('common.previewError'));
  }
};

const closePreview = () => {
  if (previewUrl.value) {
    window.URL.revokeObjectURL(previewUrl.value);
  }
  previewUrl.value = null;
  previewItem.value = null;
  previewMimeType.value = '';
};

const downloadPreviewFile = () => {
  if (previewItem.value) {
    handleDownload(previewItem.value);
  }
};

const fetchFileBlob = (item, disposition = 'attachment') => {
  const requestUrl = disposition === 'inline' ? item.preview_url : item.download_url;
  const path = item.file_path || item.file;

  if (!requestUrl && !path) {
    throw new Error(t('common.fileNotFound') || 'Archivo no disponible');
  }

  const config = {
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    responseType: 'blob'
  };

  if (requestUrl) {
    return axios.get(requestUrl, config);
  }

  return axios.get(
    `${import.meta.env.VITE_API_URL}/export/download`,
    {
      ...config,
      params: {
        path
      }
    }
  );
};

const previewFileName = computed(() => {
  return previewItem.value?.file_name || previewItem.value?.name || 'Archivo';
});

const previewKind = computed(() => {
  const fileName = previewFileName.value.toLowerCase();
  const mimeType = previewMimeType.value.toLowerCase();

  if (mimeType.includes('pdf') || fileName.endsWith('.pdf')) {
    return 'pdf';
  }

  if (mimeType.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/i.test(fileName)) {
    return 'image';
  }

  return 'unsupported';
});

const extractFileName = (path) => {
  if (!path) return '';
  try {
    return String(path).split('/').pop();
  } catch {
    return path;
  }
};

// Función para obtener el valor a mostrar, traduciendo los tipos de solicitud
const getDisplayValue = (item, fieldKey) => {
  const value = item[fieldKey];

  // Manejar múltiples tipos (array o string separado por comas) para traducciones
  if (fieldKey === 'type' && value) {
    return typeLabel(value, item?.other_action, { t, te });
  }

  // Si es el campo "status" y tenemos un valor válido, permitir múltiples estados también
  if (fieldKey === 'status' && value) {
    const statuses = Array.isArray(value)
      ? value
      : String(value).split(',').map((v) => v.trim()).filter(Boolean);

    const translated = statuses
      .map((v) => t(`common.requestStatus.${v}`) || v)
      .join(', ');

    return translated || '-';
  }

  return value || '-';
};

const getCupsDetailPath = (item) => {
  const supplyType = (item?.type || '').toLowerCase();

  if (supplyType === 'gas') {
    return `/gas-detail/${item.id}`;
  }

  if (supplyType === 'light') {
    return `/light-detail/${item.id}`;
  }

  return `/request-detail/${item.id}`;
};

const getAdminDetailPath = (item) => {
  const adminId = item?.propietario?.id || item?.propietario_id || item?.owner_id || item?.user_id || item?.cliente_id || item?.user?.id || item?.client_id || null;
  return adminId ? `/admin-detail/${adminId}` : null;
};

const handleVoidFile = (item) => {
  closeDropdown();
  emit('void-file', item);
};

const handleListOption = (item, option) => {
  closeDropdown();
  const selectedRoute = option?.key || option?.path || option?.value;
  if (!selectedRoute) {
    return;
  }

  const routesWithDetailId = [
    '/forms/change_ownership',
    // Retirada «Cambio de comercializadora» (ADS-0130): se deja comentada, no borrada, por si hubiera que reponerla.
    // '/forms/change_supplier',
    '/forms/invoice',
    '/forms/account_change',
    '/forms/other_type'
  ];

  const userId = item?.property?.owner_id || item?.user_id || item?.owner_id || item?.user?.id || null;
  const userName = item?.property?.owner_name || item?.owner_name || item?.user?.name || '';
  const query = { user_id: userId, user_name: userName };

  if (routesWithDetailId.includes(selectedRoute) && item?.id) {
    query.id = item.id;
  }

  // Merge option-specific query parameters if provided (e.g. { type: 'comunidad' }).
  // Puede ser una función de la fila: las opciones "Crear solicitud" de un
  // CUPS pasan su id y su energía al formulario.
  const optionQuery = typeof option?.query === 'function' ? option.query(item) : option?.query;
  if (optionQuery && typeof optionQuery === 'object') {
    Object.assign(query, optionQuery);
  }

  // If navigating to Property, pass search params by id so the Property view can auto-search by id
  if (selectedRoute === '/Property') {
    // prefer explicit id from item fields
    const targetId = item?.propietario?.id || item?.propietario_id || item?.user?.id || item?.user_id || userId || item?.id || '';
    if (targetId) {
      query.search_by = query.search_by || 'id';
      query.query = query.query || targetId;
      // also include explicit `id` param so views expecting `id` receive it
      query.id = query.id || targetId;
    }
  }

  // For routes that list supplies/requests, if coming from a client/admin context,
  // prefer to pass the account `id` so the target view can perform a user-scoped list
  const routesForceIdSearch = ['/luz', '/gas', '/request'];
  if (routesForceIdSearch.includes(selectedRoute)) {
    const targetId = item?.id || item?.user?.id || item?.user_id || item?.owner_id || item?.propietario?.id || '';
    if (targetId) {
      query.search_by = query.search_by || 'id';
      query.query = query.query || targetId;
      query.id = query.id || targetId;
    }
  }

  router.push({ path: selectedRoute, query });
};

const emit = defineEmits(['update:filters', 'filter-change', 'void-file']);

const props = defineProps({
  fields: {
    type: Array,
    required: true,
  },
  filterDefinitions: {
    type: Array,
    default: () => []
  },
  filters: {
    type: Object,
    default: () => ({})
  },
  filterError: {
    type: String,
    default: ''
  },
  items: {
    type: Array,
    required: true,
    default: () => []
  },
  showOptions: {
    type: Boolean,
    default: false
  },
  showViewMoreButton: {
    type: Boolean,
    default: false
  },
  route: {
    type: String,
    default: null
  },
  click: {
    type: Function,
    default: () => { }
  },
  listOptions: {
    type: Array,
    default: () => []
  },
  fileDownload: {
    type: Boolean,
    default: false
  },
  // Casillas para elegir qué exportar (ADSREC-0029): el useRowSelection de la
  // vista, envuelto en reactive(). Sin él, la tabla no lleva casillas.
  selection: {
    type: Object,
    default: null
  }
});

const filteredListOptions = computed(() => {
  return (props.listOptions || []).filter((option) => {
    const route = option?.key || option?.path || option?.value || '';
    const label = (option?.label || '').toLowerCase();
    return route !== '/forms/supplies' && label !== 'alta de suministro';
  });
});
</script>

<style scoped>
table {
  border-collapse: separate;
  border-spacing: 0 6px;
  table-layout: auto;
  min-width: 900px;
}

th {
  position: sticky;
  top: -40px;
  z-index: 10;
  white-space: nowrap;
}

tbody tr {
  background: white;
  box-shadow: 0 0 0 1px #385660;
  border-radius: 4px;
}

td {
  padding: 0.5rem 1rem;
}

/* Ajustes para columnas específicas */
th:first-child,
td:first-child {
  padding-left: 0.5rem;
}

th:last-child,
td:last-child {
  padding-right: 0.5rem;
}

/* Ajuste para columnas de opciones */
td:last-child {
  width: 10%;
  white-space: nowrap;
}

.row-check {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
  accent-color: #2AB4D9;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
